/**
 * Field validation. Returns problems keyed by field path (`title`, `variants.2.name`) with a code the dashboard
 * turns into a sentence in the editor's language; `message` carries a site-supplied text (pattern messages).
 */
import { isRequired, listItemFields, switchedFields, type Field } from './config.js';
import { parseYouTube } from './youtube.js';

export interface Problem {
  path: string;
  code: 'required' | 'pattern' | 'min' | 'max' | 'minItems' | 'maxItems' | 'number' | 'integer' | 'url' | 'youtube' | 'date' | 'option';
  message?: string;
  limit?: number;
}

export function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

export function validateFields(fields: Field[], data: Record<string, unknown> | undefined, prefix = ''): Problem[] {
  const out: Problem[] = [];
  for (const field of fields) {
    if (field.type === 'hidden') continue;
    const path = prefix ? `${prefix}.${field.name}` : field.name;
    out.push(...validateValue(field, data?.[field.name], path));
  }
  return out;
}

export function validateValue(field: Field, value: unknown, path: string): Problem[] {
  const out: Problem[] = [];
  if (isEmpty(value)) {
    if (isRequired(field) && field.type !== 'object') out.push({ path, code: 'required' });
    if (field.type === 'object' && isRequired(field) && value === undefined) {
      // A required object that's missing: validate its fields as empty.
      out.push(...validateFields(field.fields, {}, path));
    }
    return out;
  }
  switch (field.type) {
    case 'string':
    case 'text':
    case 'url':
    case 'youtube':
    case 'color': {
      const s = String(value);
      if (field.pattern) {
        try {
          if (!new RegExp(field.pattern[0], 'u').test(s)) out.push({ path, code: 'pattern', message: field.pattern[1] });
        } catch {
          /* bad pattern in config: ignore */
        }
      }
      if ('max_length' in field && field.max_length && s.length > field.max_length) {
        out.push({ path, code: 'max', limit: field.max_length });
      }
      if (field.type === 'url' && !field.pattern && !/^(https?:\/\/|mailto:|\/|#)/.test(s)) out.push({ path, code: 'url' });
      if (field.type === 'youtube' && !field.pattern && !parseYouTube(s)) out.push({ path, code: 'youtube' });
      break;
    }
    case 'number': {
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n)) out.push({ path, code: 'number' });
      else {
        if (field.value_type === 'int' && !Number.isInteger(n)) out.push({ path, code: 'integer' });
        if (field.min !== undefined && n < field.min) out.push({ path, code: 'min', limit: field.min });
        if (field.max !== undefined && n > field.max) out.push({ path, code: 'max', limit: field.max });
      }
      break;
    }
    case 'date':
    case 'datetime':
      if (Number.isNaN(Date.parse(String(value)))) out.push({ path, code: 'date' });
      break;
    case 'gallery': {
      const list = Array.isArray(value) ? value : [];
      if (field.min && list.length < field.min) out.push({ path, code: 'minItems', limit: field.min });
      if (field.max && list.length > field.max) out.push({ path, code: 'maxItems', limit: field.max });
      break;
    }
    case 'select': {
      const values = field.multiple ? (Array.isArray(value) ? value : [value]) : [value];
      const allowed = new Set(field.options.map((o) => String(o.value)));
      if (values.some((v) => !allowed.has(String(v)))) out.push({ path, code: 'option' });
      if (field.multiple && field.min && values.length < field.min) out.push({ path, code: 'minItems', limit: field.min });
      if (field.multiple && field.max && values.length > field.max) out.push({ path, code: 'maxItems', limit: field.max });
      break;
    }
    case 'list': {
      const list = Array.isArray(value) ? value : [];
      if (field.min && list.length < field.min) out.push({ path, code: 'minItems', limit: field.min });
      if (field.max && list.length > field.max) out.push({ path, code: 'maxItems', limit: field.max });
      list.forEach((item, i) => {
        const itemFields = listItemFields(field, item);
        if (itemFields) out.push(...validateFields(field.fields ? switchedFields(itemFields, field.switch, item as Record<string, unknown>) : itemFields, item as Record<string, unknown>, `${path}.${i}`));
        else if (field.field) out.push(...validateValue(field.field, item, `${path}.${i}`));
      });
      break;
    }
    case 'object': {
      // With a switch, only the chosen option's fields count.
      out.push(...validateFields(switchedFields(field.fields, field.switch, value as Record<string, unknown>), value as Record<string, unknown>, path));
      break;
    }
  }
  return out;
}
